// Pure helpers for slicing the support-ticket feed into the three
// buckets the Live Chat panel renders. Kept out of App.tsx so the
// rules are unit-testable without React state in the loop.
//
// Buckets:
//   incoming     — status='Live Chat' AND no assignedTo. Visible to
//                  everyone with support:assign so anyone can claim.
//   mine         — status='Live Chat' AND assignedTo === currentUser.
//   otherClaimed — status='Live Chat' AND assignedTo set AND not me.
//                  Hidden from the UI so chats one teammate is on
//                  don't crowd another teammate's queue.

export interface MinTicket {
  id: string;
  status: string;
  assignedTo?: string;
}

export interface LiveChatBuckets<T extends MinTicket> {
  incoming: T[];
  mine: T[];
  otherClaimed: T[];
}

export function splitLiveChats<T extends MinTicket>(
  tickets: T[],
  currentUserId: string | null,
): LiveChatBuckets<T> {
  const live = tickets.filter((t) => t.status === "Live Chat");
  const incoming: T[] = [];
  const mine: T[] = [];
  const otherClaimed: T[] = [];
  for (const t of live) {
    if (!t.assignedTo) {
      incoming.push(t);
    } else if (currentUserId && t.assignedTo === currentUserId) {
      mine.push(t);
    } else {
      otherClaimed.push(t);
    }
  }
  return { incoming, mine, otherClaimed };
}

export interface ReassignTarget {
  id: string;
  fullName: string;
  firstName: string;
}

/**
 * Pick the candidates for reassign. Excludes the current operator
 * (you can't hand off to yourself) and inactive users. Pulls a
 * displayable first name from each candidate's fullName so the
 * customer-facing system message reads naturally.
 */
export function eligibleReassignTargets(
  team: ReadonlyArray<{ id: string; fullName: string; active: boolean; roles?: string[] }>,
  currentUserId: string,
): ReassignTarget[] {
  return team
    .filter((u) => u.active && u.id !== currentUserId)
    .map((u) => ({
      id: u.id,
      fullName: u.fullName,
      firstName: extractFirstName(u.fullName),
    }));
}

function extractFirstName(fullName: string): string {
  const trimmed = (fullName ?? "").trim();
  if (!trimmed) return "Operator";
  const first = trimmed.split(/\s+/)[0];
  return first || "Operator";
}
